import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiError, WindowContext } from '../api';
import { endpoints } from '../api';
import { usePermissions } from '../session';
import { Button, ErrorNote } from '../ui';

/**
 * Opening the register: which reader and printers this session uses
 * (defaults from the register), then the cashier opens a drawer with a
 * counted float on the Drawer tab. Shown full-screen until a session exists.
 */
export function RegisterGate({ context }: { context: WindowContext }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [registerId, setRegisterId] = useState(context.register?.id ?? context.registers[0]?.id ?? '');
  const register = context.registers.find((r) => r.id === registerId) ?? context.register;
  const [terminalId, setTerminalId] = useState<string | null | undefined>(undefined);
  const [ticketPrinterId, setTicketPrinterId] = useState<string | null | undefined>(undefined);
  const [receiptPrinterId, setReceiptPrinterId] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const open = useMutation({
    mutationFn: () => endpoints.openSession({ register_id: context.register ? undefined : registerId, payment_terminal_id: terminalId, ticket_printer_id: ticketPrinterId, receipt_printer_id: receiptPrinterId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['context'] }),
    onError: (cause: ApiError) => setError(cause.message),
  });

  return (
    <div className="mx-auto max-w-lg rounded-xl border border-line bg-surface p-6">
      <h1 className="text-xl font-semibold">Open the register</h1>
      <p className="mt-1 text-sm text-ink-400">{context.device.name} · {context.venue?.name ?? 'no venue'}</p>
      {error ? (
        <div className="mt-4">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      {!context.register && context.registers.length === 0 ? <p className="mt-4 text-sm text-bad">This device has no register and the venue has none to choose from. An admin creates registers under Venues → Box office.</p> : null}
      <div className="mt-5 space-y-4 text-sm">
        {!context.register ? (
          <label className="block">
            <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Register</span>
            <select value={registerId} onChange={(e) => setRegisterId(e.target.value)} className="touch mt-1 w-full rounded-lg border border-line px-3">
              {context.registers.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="block">
          <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Card reader</span>
          <select value={terminalId === undefined ? (register?.default_payment_terminal_id ?? '') : (terminalId ?? '')} onChange={(e) => setTerminalId(e.target.value || null)} className="touch mt-1 w-full rounded-lg border border-line px-3">
            <option value="">No reader (cash, check, keyed card only)</option>
            {context.terminals.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
                {t.simulated ? ' (simulated)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Ticket printer</span>
          <select value={ticketPrinterId === undefined ? (register?.default_ticket_printer_id ?? '') : (ticketPrinterId ?? '')} onChange={(e) => setTicketPrinterId(e.target.value || null)} className="touch mt-1 w-full rounded-lg border border-line px-3">
            <option value="">No ticket printer</option>
            {context.printers
              .filter((p) => p.printer_model === 'BOCA_FGL')
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.last_status ? ` · ${p.last_status}` : ''}
                </option>
              ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Receipt printer</span>
          <select value={receiptPrinterId === undefined ? (register?.default_receipt_printer_id ?? '') : (receiptPrinterId ?? '')} onChange={(e) => setReceiptPrinterId(e.target.value || null)} className="touch mt-1 w-full rounded-lg border border-line px-3">
            <option value="">No receipt printer</option>
            {context.printers
              .filter((p) => p.printer_model !== 'BOCA_FGL')
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
      </div>
      <div className="mt-6">
        {can('box_office.session.open') ? (
          <Button variant="primary" className="touch w-full text-base" disabled={open.isPending || (!context.register && !registerId)} onClick={() => open.mutate()}>
            Open register
          </Button>
        ) : (
          <p className="text-sm text-bad">Your role cannot open a register; ask a manager.</p>
        )}
      </div>
    </div>
  );
}
